/**
 * Query string helpers: params table ↔ URL bar stay in sync.
 */

function safeDecode(segment) {
  try {
    return decodeURIComponent(segment.replace(/\+/g, ' '));
  } catch {
    return segment;
  }
}

/**
 * @param {string} query - string after "?" (no leading ?)
 * @returns {{ key: string, value: string }[]}
 */
export function parseQueryString(query) {
  if (!query) return [];
  const params = [];
  for (const part of query.split('&')) {
    if (!part) continue;
    const eq = part.indexOf('=');
    if (eq === -1) {
      params.push({ key: safeDecode(part), value: '' });
    } else {
      params.push({
        key: safeDecode(part.slice(0, eq)),
        value: safeDecode(part.slice(eq + 1)),
      });
    }
  }
  return params;
}

/**
 * new URL() lowercases the hostname, which corrupts {{Variable}} placeholders that
 * happen to sit in the host portion of the URL.  This restores the original casing of
 * every {{...}} token by replacing, in order, each lowercased placeholder in the
 * parsed string with the corresponding original token from the raw input.
 */
export function restoreVariableCase(parsed, original) {
  const origTokens = [];
  const scanRe = /\{\{([^}]*)\}\}/g;
  let m;
  while ((m = scanRe.exec(original)) !== null) {
    origTokens.push(m[0]);
  }
  let idx = 0;
  return parsed.replace(/\{\{([^}]*)\}\}/g, () =>
    idx < origTokens.length ? origTokens[idx++] : '{{}}'
  );
}

/**
 * Split URL into base (no query) and query param key/value pairs.
 */
export function splitUrlAndParams(urlString) {
  const raw = urlString ?? '';
  if (!raw) return { baseUrl: '', params: [] };

  try {
    const u = new URL(raw);
    // Restore {{ }} variable placeholders that new URL() percent-encodes in the pathname
    // and lowercases when they appear in the hostname.
    const pathname = u.pathname.replace(/%7B/gi, '{').replace(/%7D/gi, '}');
    if (!u.search || u.search === '?') {
      const baseUrl = restoreVariableCase(`${u.origin}${pathname}${u.hash || ''}`, raw);
      return { baseUrl, params: [] };
    }
    const params = [];
    u.searchParams.forEach((value, key) => {
      params.push({ key, value });
    });
    return {
      baseUrl: restoreVariableCase(`${u.origin}${pathname}${u.hash || ''}`, raw),
      params,
    };
  } catch {
    const hashIdx = raw.indexOf('#');
    const withoutHash = hashIdx === -1 ? raw : raw.slice(0, hashIdx);
    const hash = hashIdx === -1 ? '' : raw.slice(hashIdx);
    const q = withoutHash.indexOf('?');
    if (q === -1) return { baseUrl: raw, params: [] };
    return {
      baseUrl: withoutHash.slice(0, q) + hash,
      params: parseQueryString(withoutHash.slice(q + 1)),
    };
  }
}

// Minimal query-param encoding: only encode chars that break URL structure.
// ( ) , @ : and other RFC 3986 sub-delims are left as typed.
//
// Spaces are the exception, and they are deliberately split by destination:
// the URL bar and the params table show them exactly as typed, because
// `filter=and(le(Dokumentnummer, 0))` is what the user wrote and turning it
// into `%20` mid-edit is unreadable. The request that goes on the wire still
// encodes them, since a literal space is not valid in a request-target.
function encodeParam(str, { encodeSpaces = true } = {}) {
  const encoded = str
    .replace(/%(?![0-9A-Fa-f]{2})/g, '%25') // lone % not already part of %XX
    .replace(/&/g, '%26')
    .replace(/#/g, '%23');

  return encodeSpaces ? encoded.replace(/ /g, '%20') : encoded;
}

/**
 * Build full URL from base + enabled param rows (keys required).
 * @param {string} baseUrl
 * @param {{ key: string, value?: string, enabled?: boolean }[]} paramRows
 */
export function composeUrlWithParams(baseUrl, paramRows, { encodeSpaces = true } = {}) {
  const base = (baseUrl ?? '').trimEnd();
  const enabled = (paramRows || []).filter((p) => p?.key && p.enabled !== false);
  if (enabled.length === 0) return base;

  const qs = enabled
    .map((p) => {
      const k = encodeParam(p.key, { encodeSpaces });
      const v = encodeParam(p.value ?? '', { encodeSpaces });
      // Omit trailing "=" when value is empty so mid-typing a key
      // (e.g. "json") never becomes "json=" in the URL bar.
      return v ? `${k}=${v}` : k;
    })
    .join('&');

  const qIndex = base.indexOf('?');
  const sep = qIndex === -1 ? '?' : '&';
  return `${base}${sep}${qs}`;
}

/** Recompose URL from current base + param rows (used when params change). */
export function urlFromParams(currentUrl, paramRows) {
  const { baseUrl } = splitUrlAndParams(currentUrl);
  // Display path: spaces stay as typed.
  return composeUrlWithParams(baseUrl, paramRows, { encodeSpaces: false });
}

/**
 * URL used for the actual request.
 * When the params table has enabled keys, query comes from params (URL bar base only).
 * When the table is empty, the URL bar string is used as-is (including any query typed there).
 */
export function buildRequestUrl(urlString, paramRows) {
  const enabled = (paramRows || []).filter((p) => p?.key && p.enabled !== false);
  if (enabled.length === 0) {
    // Straight from the URL bar, where spaces are shown as typed — encode them
    // here so the request-target is valid.
    return (urlString ?? '').replace(/ /g, '%20');
  }
  const { baseUrl } = splitUrlAndParams(urlString ?? '');
  return composeUrlWithParams(baseUrl.replace(/ /g, '%20'), enabled);
}

/**
 * Prefer stored param rows when present; otherwise parse from URL query.
 * @returns {{ displayUrl: string, paramRows: { key: string, value: string, enabled: boolean }[] }}
 */
export function normalizeRequestUrlAndParams(rawUrl, storedParams) {
  const { params: urlParsed } = splitUrlAndParams(rawUrl || '');
  const hasStoredKeys = (storedParams || []).some((p) => p?.key);
  let paramRows;
  if (hasStoredKeys) {
    paramRows = (storedParams || []).map((p) => ({
      key: p.key ?? '',
      value: p.value ?? '',
      enabled: p.enabled !== false,
    }));
  } else {
    paramRows = urlParsed.map((p) => ({
      key: p.key,
      value: p.value ?? '',
      enabled: true,
    }));
  }
  const displayUrl = urlFromParams(rawUrl || '', paramRows);
  return { displayUrl, paramRows };
}
